import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  getSessionLimitExceededMessage,
  MAX_PROVIDERS_PER_SESSION,
} from '../../constants/subscriptions';
import { storeProvidersInCache } from '../../utils/serviceCalls/cache';
import { generateUniqueId } from '../../utils/utilities';
import { isDuplicateProvider } from '../../utils/validation';
import { useAuthenticatedUser } from '../Auth';
import CheckoutSection from './CheckoutSection';
import HeroSection from './HeroSection';
import InstructionsSection from './InstructionsSection';
import ProviderRoster from './ProviderRoster';
import ProviderSearch from './ProviderSearch';

/**
 * @typedef {import('./types').RosterProvider} RosterProvider
 * @typedef {import('../../utils/serviceCalls/cache').SelectedProvider} SelectedProvider
 * @typedef {'idle'|'processing'|'navigating'|'error'} CheckoutStatus
 */

/**
 * Handles the checkout process for roster providers.
 * Stores providers in cache and navigates to checkout page.
 *
 * @param {RosterProvider[]} rosterProviders - List of providers to checkout
 * @param {string} userId - ID of the user making the purchase
 */
export const useProviderCheckout = (rosterProviders, userId) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState(/** @type {CheckoutStatus} */ ('idle'));
  const [checkoutError, setError] = useState('');

  const clearError = useCallback(() => {
    setError('');
    setStatus((s) => (s === 'error' ? 'idle' : s));
  }, []);

  const checkingOut = status === 'processing' || status === 'navigating';

  // Avoid state updates after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Clear checkout errors when roster changes
  const prevRosterProvidersRef = useRef(rosterProviders);
  useEffect(() => {
    if (prevRosterProvidersRef.current !== rosterProviders) {
      prevRosterProvidersRef.current = rosterProviders;
      if (status === 'error') {
        clearError();
      }
    }
  }, [status, clearError, rosterProviders]);

  const checkout = useCallback(async () => {
    if (!Array.isArray(rosterProviders) || rosterProviders.length === 0) {
      setError('Please add at least one provider before continuing.');
      return false;
    }

    if (rosterProviders.length > MAX_PROVIDERS_PER_SESSION) {
      setError(getSessionLimitExceededMessage(rosterProviders.length));
      return false;
    }

    setStatus('processing');
    setError('');

    try {
      const cacheId = generateUniqueId();

      /** @type {SelectedProvider[]} */
      const selectedProviders = rosterProviders.map((p) => ({
        pwid: p.providerCode,
        last4: p.last4,
        email: p.email,
        providerName:
          p.displayName ||
          [p.firstName, p.lastName].filter(Boolean).join(' ') ||
          '',
      }));

      const response = await storeProvidersInCache(
        cacheId,
        userId,
        selectedProviders,
      );

      if (!response?.success) {
        throw new Error('Unable to proceed to checkout. Please try again.');
      }

      // Preserve session in URL so Back button returns to same session
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('session', cacheId);
          return next;
        },
        { replace: true },
      );

      // Navigate to checkout
      setStatus('navigating');
      navigate(`/checkout?session=${encodeURIComponent(cacheId)}`);

      return true;
    } catch (err) {
      console.error('Error during checkout:', err);
      const message = 'Unable to proceed to checkout. Please try again.';
      if (mountedRef.current) {
        setError(message);
        setStatus('error');
      }
      return false;
    }
  }, [userId, navigate, setSearchParams, rosterProviders]);

  return {
    checkout,
    checkoutError,
    checkingOut,
  };
};

/**
 * Page for building a roster of providers to sponsor with Elevated Profile subscriptions.
 *
 * User Flow:
 * 1. Search for provider by NPI or name/location
 * 2. Validate provider identity with last 4 digits of DEA/license
 * 3. Provide provider's work email address
 * 4. Add provider to roster
 * 5. Repeat for additional providers
 * 6. Continue to checkout to complete purchase
 */
const AddProvidersToRosterPage = () => {
  const { userId, fullName } = useAuthenticatedUser();

  const [rosterProviders, setRosterProviders] = useState(
    /** @type {RosterProvider[]} */ ([]),
  );

  // Placeholder for a useEffect that fetches existing roster providers on mount.
  // Existing roster providers from backend (providers already in user's roster)
  // This should be fetched from an API endpoint that returns current roster providers
  // Example: GET /api/elevatedroster/providers or similar endpoint
  // The response should include providerCode for each provider
  // This is used to prevent adding providers that are already in the roster
  const [existingRosterProviders /** setExistingRosterProviders */] = useState(
    /** @type {Array<{providerCode?: string}>} */ ([]),
  );

  const { checkout, checkoutError, checkingOut } = useProviderCheckout(
    rosterProviders,
    userId,
  );

  const handleAddProvider = useCallback(
    /** @param {RosterProvider} provider */
    (provider) => {
      if (!provider) return;
      setRosterProviders((prevProviders) => {
        if (isDuplicateProvider(provider.providerCode, prevProviders)) {
          return prevProviders;
        }

        return [...prevProviders, provider];
      });
    },
    [],
  );

  return (
    <div className="w-full bg-gray-20 font-hg-sans text-gray-90">
      <HeroSection adminName={fullName} />

      <section className="relative px-0 mt-0 md:px-25 md:-mt-[264px]">
        <div className="mx-auto w-full bg-white z-10 px-4 py-7 md:px-7 md:py-10 md:rounded-xl md:shadow-lg">
          <InstructionsSection />

          <div data-testid="provider-search-section">
            <ProviderSearch
              onAdd={handleAddProvider}
              disabled={checkingOut}
              existingProviders={rosterProviders}
              existingRosterProviders={existingRosterProviders}
            />
          </div>

          <div data-testid="provider-roster-section" className="pb-15 md:pb-10">
            <ProviderRoster
              providers={rosterProviders}
              onProvidersChange={setRosterProviders}
              disabled={checkingOut}
            />
          </div>
        </div>
      </section>
      <section className="w-full h-25 relative" />
      {/* Sticky checkout section - full width outside container */}
      <div
        data-testid="checkout-section"
        className="sticky bottom-0 w-full bg-white border-t border-gray-30 shadow-lg z-50"
      >
        <div className="max-w-7xl ml-auto mr-0 px-4 py-4 md:px-7">
          <CheckoutSection
            providers={rosterProviders}
            checkingOut={checkingOut}
            checkoutError={checkoutError}
            onCheckout={checkout}
          />
        </div>
      </div>
    </div>
  );
};

export default AddProvidersToRosterPage;
